import type {BigBagResumen, CambiosDeBigBag} from '@cnc/contracts';
import {useRef, useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useEditarCeldaDeBigBag, useFiltrosDeInventario} from '../api/inventario';
import {tr, trx} from '../i18n/idioma';
import {convertirValor, opcionesDeListas, tipoDeEntrada, valorInicial, type CampoBigBag} from './campos-big-bag';

// Edicion rapida de la tabla del inventario (6/10/2026): clic en la celda,
// se escribe y con Enter (o al salir) se guarda ese solo dato con la version
// de la fila; Esc cancela. Las mismas reglas que la ficha del Big Bag.
export function CeldaDeBigBag({fila, campo, texto}: {fila: BigBagResumen; campo: CampoBigBag; texto: string}) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [estado, setEstado] = useState<'quieta' | 'guardando' | 'guardada' | 'error'>('quieta');
  const [mensaje, setMensaje] = useState<string | null>(null);
  const editar = useEditarCeldaDeBigBag();
  // Enter o Esc cierran la celda y el navegador puede disparar ademas "salir del campo": solo cuenta la primera.
  const abierta = useRef(false);

  const abrir = () => {
    setValor(valorInicial(fila, campo.campo));
    setMensaje(null);
    abierta.current = true;
    setEditando(true);
  };
  const guardar = async () => {
    if (!abierta.current) return;
    abierta.current = false;
    setEditando(false);
    const inicial = valorInicial(fila, campo.campo);
    if (valor.trim() === inicial) return;
    const convertido = convertirValor(campo, valor);
    if ('error' in convertido) {
      setEstado('error');
      setMensaje(convertido.error);
      return;
    }
    if (campo.tipo === 'numero' && convertido.valor === Number(inicial)) return;
    setEstado('guardando');
    try {
      const r = await editar.mutateAsync({
        bbNo: fila.bbNo,
        edicion: {version: fila.version, cambios: {[campo.campo]: convertido.valor} as CambiosDeBigBag},
      });
      setEstado('guardada');
      setMensaje(r.avisos.length ? r.avisos.join(' ') : null);
    } catch (e) {
      setEstado('error');
      setMensaje(
        e instanceof ErrorDeApi
          ? [e.message, ...(e.detalles ?? [])].join(' ')
          : tr('No se pudo guardar.', 'Could not save.'),
      );
    }
  };

  if (editando)
    return (
      <input
        autoFocus
        aria-label={tr(`${trx(campo.etiqueta)} de ${fila.bbNo}`, `${trx(campo.etiqueta)} of ${fila.bbNo}`)}
        type={tipoDeEntrada(campo.tipo)}
        inputMode={campo.tipo === 'numero' ? 'decimal' : undefined}
        list={campo.lista ? `opciones-bb-${campo.lista}` : undefined}
        value={valor}
        onChange={e => setValor(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void guardar();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            abierta.current = false;
            setEditando(false);
          }
        }}
        onBlur={() => void guardar()}
        className="h-7 w-full min-w-24 rounded border border-verde px-1.5 text-sm"
      />
    );
  const tono =
    estado === 'error'
      ? 'border-alerta text-alerta'
      : estado === 'guardada'
        ? mensaje
          ? 'border-dorado'
          : 'border-verde'
        : estado === 'guardando'
          ? 'border-linea opacity-60'
          : 'border-transparent hover:border-verde/60';
  return (
    <button
      type="button"
      onClick={abrir}
      aria-label={`${trx(campo.etiqueta)}: ${texto}`}
      title={mensaje ?? tr('Clic para editar', 'Click to edit')}
      className={`block h-7 w-full cursor-text truncate rounded border border-dashed px-1.5 text-left ${tono}`}
    >
      {estado === 'guardando' ? '…' : texto}
      {(estado === 'error' || (estado === 'guardada' && mensaje)) && ' ⚠'}
    </button>
  );
}

// Listas de opciones (minas, proveedores...) para las celdas: una sola vez por pantalla.
export function OpcionesDeBigBags() {
  const listas = opcionesDeListas(useFiltrosDeInventario({estado: 'todos'}).data);
  return (
    <>
      {Object.entries(listas).map(([clave, valores]) => (
        <datalist key={clave} id={`opciones-bb-${clave}`}>
          {valores.map(v => (
            <option key={v} value={v} />
          ))}
        </datalist>
      ))}
    </>
  );
}
